/**
 * 🚀 UNIVERSAL COUNTDOWN BAR 2025
 * 
 * Countdown Bar Element mit Text + Timer
 * - Zwei Modi: Fixed Date oder Monthly Reset
 * - Separat stylebar: Text und Countdown
 * - Optimistic UI mit Debouncing
 * - Eigene Floating Toolbar
 */

import React, { useState, useEffect, useRef } from 'react';
import CountdownBarFloatingToolbar from './CountdownBarFloatingToolbar';
import { useDebouncedCallback } from '../../hooks/useDebounce';
import './UniversalCountdownBar.css';

function UniversalCountdownBar({
    element,
    isEditMode,
    isMobileLayout,
    onUpdate,
    onSelect,
    colors = {},
    fonts = {},
    isCurrentlyEditing = false,
    onEditingStart = () => {},
    onEditingEnd = () => {}
}) {
    const countdownRef = useRef(null);
    const [showToolbar, setShowToolbar] = useState(false);
    const [timeLeft, setTimeLeft] = useState({ days: 0, hours: 0, minutes: 0, seconds: 0 });
    const [editingText, setEditingText] = useState(null); // ✅ Lokaler State während Text-Edit

    // ⚡ OPTIMISTIC UI: Lokaler State für Slider-Werte (SOFORTIGES Feedback)
    const [localStyles, setLocalStyles] = useState({
        // Bar Styles
        barHeight: element.barHeight,
        barHeightMobile: element.barHeightMobile,
        barBorderRadius: element.barBorderRadius,
        barBorderRadiusMobile: element.barBorderRadiusMobile,
        barPadding: element.barPadding,
        barPaddingMobile: element.barPaddingMobile,
        
        // Text Styles
        textFontSize: element.textFontSize,
        textFontSizeMobile: element.textFontSizeMobile,
        textFontWeight: element.textFontWeight,
        textFontWeightMobile: element.textFontWeightMobile,
        
        // Countdown Styles
        countdownFontSize: element.countdownFontSize,
        countdownFontSizeMobile: element.countdownFontSizeMobile,
        countdownFontWeight: element.countdownFontWeight,
        countdownFontWeightMobile: element.countdownFontWeightMobile,
        
        // Timer Container Styles
        timerContainerPadding: element.timerContainerPadding,
        timerContainerPaddingMobile: element.timerContainerPaddingMobile,
        timerContainerBorderRadius: element.timerContainerBorderRadius,
        timerContainerBorderRadiusMobile: element.timerContainerBorderRadiusMobile,
        timerContainerBorderWidth: element.timerContainerBorderWidth,
        timerShadowIntensity: element.timerShadowIntensity,
        timerShadowDistance: element.timerShadowDistance,
        timerShadowOpacity: element.timerShadowOpacity,
        timerGap: element.timerGap,
        timerGapMobile: element.timerGapMobile,
        
        // Margins
        marginTop: element.marginTop,
        marginBottom: element.marginBottom,
        marginLeft: element.marginLeft,
        marginRight: element.marginRight,
        marginTopMobile: element.marginTopMobile,
        marginBottomMobile: element.marginBottomMobile,
        marginLeftMobile: element.marginLeftMobile,
        marginRightMobile: element.marginRightMobile,
        
        // Shadow
        shadowIntensity: element.shadowIntensity,
        shadowDistance: element.shadowDistance,
        shadowOpacity: element.shadowOpacity
    });

    // ⚡ Debounced Parent Update (300ms für Firebase)
    const debouncedParentUpdate = useDebouncedCallback((updates) => {
        if (onUpdate) {
            onUpdate(updates);
        }
    }, 300);

    // 🕐 Countdown Calculation
    useEffect(() => {
        const calculateTimeLeft = () => {
            const countdownMode = element.countdownMode || 'fixed'; // 'fixed' or 'monthly'
            let targetDate;

            if (countdownMode === 'fixed') {
                // Fixed Date Mode
                targetDate = element.targetDate ? new Date(element.targetDate) : new Date();
            } else {
                // Monthly Reset Mode - zählt bis zum ersten des nächsten Monats
                const now = new Date();
                targetDate = new Date(now.getFullYear(), now.getMonth() + 1, 1, 0, 0, 0);
            }

            const now = new Date().getTime();
            const distance = targetDate.getTime() - now;

            if (distance < 0) {
                // Countdown abgelaufen
                setTimeLeft({ days: 0, hours: 0, minutes: 0, seconds: 0 });
                return;
            }

            const days = Math.floor(distance / (1000 * 60 * 60 * 24));
            const hours = Math.floor((distance % (1000 * 60 * 60 * 24)) / (1000 * 60 * 60));
            const minutes = Math.floor((distance % (1000 * 60 * 60)) / (1000 * 60));
            const seconds = Math.floor((distance % (1000 * 60)) / 1000);

            setTimeLeft({ days, hours, minutes, seconds });
        };

        calculateTimeLeft();
        const interval = setInterval(calculateTimeLeft, 1000);

        return () => clearInterval(interval);
    }, [element.countdownMode, element.targetDate]);

    // 🎨 Handle Click - Toggle Toolbar
    const handleClick = (e) => {
        if (!isEditMode) return;
        
        e.preventDefault();
        e.stopPropagation();
        
        if (isCurrentlyEditing) {
            // TOGGLE: Schließe Toolbar wenn bereits geöffnet
            handleToolbarClose();
        } else {
            // Öffne Toolbar
            onEditingStart();
            setShowToolbar(true);
            if (onSelect) onSelect();
        }
    };

    const handleToolbarClose = () => {
        setShowToolbar(false);
        onEditingEnd();
    };

    // ⚡ Style Change Handler
    const handleStyleChange = (updates) => {
        console.log('🎨 [CountdownBar] Style change:', updates);
        
        // Liste aller Slider-Keys die optimistisch sein sollen
        const sliderKeys = [
            'barHeight', 'barHeightMobile',
            'barBorderRadius', 'barBorderRadiusMobile',
            'barPadding', 'barPaddingMobile',
            'textFontSize', 'textFontSizeMobile',
            'textFontWeight', 'textFontWeightMobile',
            'countdownFontSize', 'countdownFontSizeMobile',
            'countdownFontWeight', 'countdownFontWeightMobile',
            'timerContainerPadding', 'timerContainerPaddingMobile',
            'timerContainerBorderRadius', 'timerContainerBorderRadiusMobile',
            'timerContainerBorderWidth',
            'timerShadowIntensity', 'timerShadowDistance', 'timerShadowOpacity',
            'timerGap', 'timerGapMobile',
            'marginTop', 'marginBottom', 'marginLeft', 'marginRight',
            'marginTopMobile', 'marginBottomMobile', 'marginLeftMobile', 'marginRightMobile',
            'shadowIntensity', 'shadowDistance', 'shadowOpacity'
        ];
        
        const updatedKeys = Object.keys(updates);
        const hasSliderValue = updatedKeys.some(key => sliderKeys.includes(key));
        
        if (hasSliderValue) {
            // ⚡ SLIDER VALUES: Optimistic Local State + Debounced Parent
            const localUpdates = {};
            sliderKeys.forEach(key => {
                if (updates[key] !== undefined) {
                    localUpdates[key] = updates[key];
                }
            });
            
            console.log('⚡ [OPTIMISTIC] Updating local styles:', localUpdates);
            setLocalStyles(prev => ({ ...prev, ...localUpdates }));
            
            console.log('💾 [DEBOUNCED] Scheduling Firebase update...');
            debouncedParentUpdate({ ...element, ...updates });
        } else {
            // ✅ NON-SLIDER VALUES: Sofort speichern
            console.log('✅ [INSTANT] Non-slider value, saving immediately:', updates);
            if (onUpdate) {
                onUpdate({ ...element, ...updates });
            }
        }
    };

    // 🎨 Handle Text Edit (ContentEditable)
    const handleTextFocus = () => {
        setEditingText(element.text || 'Limited Time Offer!');
    };
    
    const handleTextInput = (e) => {
        setEditingText(e.target.textContent);
    };
    
    const handleTextBlur = (e) => {
        const newText = e.target.textContent;
        if (newText !== element.text) {
            onUpdate({ ...element, text: newText });
        }
        setEditingText(null); // ✅ Reset local state
    };

    // ⚡ OPTIMISTIC ELEMENT: Merge element mit localStyles für Toolbar
    const optimisticElement = {
        ...element,
        ...localStyles
    };

    // 🎨 Active Values (OPTIMISTIC)
    const activeBarHeight = isMobileLayout 
        ? (localStyles.barHeightMobile ?? element.barHeightMobile ?? localStyles.barHeight ?? element.barHeight ?? 80)
        : (localStyles.barHeight ?? element.barHeight ?? 100);
    
    const activeBarBorderRadius = isMobileLayout
        ? (localStyles.barBorderRadiusMobile ?? element.barBorderRadiusMobile ?? localStyles.barBorderRadius ?? element.barBorderRadius ?? 12)
        : (localStyles.barBorderRadius ?? element.barBorderRadius ?? 12);
    
    const activeBarPadding = isMobileLayout
        ? (localStyles.barPaddingMobile ?? element.barPaddingMobile ?? localStyles.barPadding ?? element.barPadding ?? 20)
        : (localStyles.barPadding ?? element.barPadding ?? 20);

    const activeTextFontSize = isMobileLayout
        ? (localStyles.textFontSizeMobile ?? element.textFontSizeMobile ?? localStyles.textFontSize ?? element.textFontSize ?? 16)
        : (localStyles.textFontSize ?? element.textFontSize ?? 18);
    
    console.log('📏 [UniversalCountdownBar] Active Text Font Size:', {
        isMobileLayout,
        localStylesTextFontSize: localStyles.textFontSize,
        localStylesTextFontSizeMobile: localStyles.textFontSizeMobile,
        elementTextFontSize: element.textFontSize,
        elementTextFontSizeMobile: element.textFontSizeMobile,
        activeTextFontSize
    });

    const activeTextFontWeight = isMobileLayout
        ? (localStyles.textFontWeightMobile ?? element.textFontWeightMobile ?? localStyles.textFontWeight ?? element.textFontWeight ?? '500')
        : (localStyles.textFontWeight ?? element.textFontWeight ?? '500');

    const activeCountdownFontSize = isMobileLayout
        ? (localStyles.countdownFontSizeMobile ?? element.countdownFontSizeMobile ?? localStyles.countdownFontSize ?? element.countdownFontSize ?? 28)
        : (localStyles.countdownFontSize ?? element.countdownFontSize ?? 32);

    const activeCountdownFontWeight = isMobileLayout
        ? (localStyles.countdownFontWeightMobile ?? element.countdownFontWeightMobile ?? localStyles.countdownFontWeight ?? element.countdownFontWeight ?? '700')
        : (localStyles.countdownFontWeight ?? element.countdownFontWeight ?? '700');

    // 🎨 Bar Styles
    const barStyles = {
        height: `${activeBarHeight}px`,
        borderRadius: `${activeBarBorderRadius}px`,
        padding: `${activeBarPadding}px`,
        background: element.barGradientEnabled 
            ? `linear-gradient(135deg, ${element.barGradientColor1 || '#667eea'} 0%, ${element.barGradientColor2 || '#764ba2'} 100%)`
            : (element.barBackgroundColor || '#667eea'),
        border: element.barBorderEnabled 
            ? `${element.barBorderWidth || 2}px solid ${element.barBorderColor || '#ffffff'}`
            : 'none',
        boxShadow: element.barShadowEnabled 
            ? `0 ${localStyles.shadowDistance ?? element.shadowDistance ?? 8}px ${localStyles.shadowIntensity ?? element.shadowIntensity ?? 20}px rgba(0, 0, 0, ${(localStyles.shadowOpacity ?? element.shadowOpacity ?? 20) / 100})`
            : 'none',
        marginTop: `${isMobileLayout ? (localStyles.marginTopMobile ?? element.marginTopMobile ?? localStyles.marginTop ?? element.marginTop ?? 0) : (localStyles.marginTop ?? element.marginTop ?? 0)}px`,
        marginBottom: `${isMobileLayout ? (localStyles.marginBottomMobile ?? element.marginBottomMobile ?? localStyles.marginBottom ?? element.marginBottom ?? 0) : (localStyles.marginBottom ?? element.marginBottom ?? 0)}px`,
        marginLeft: `${isMobileLayout ? (localStyles.marginLeftMobile ?? element.marginLeftMobile ?? localStyles.marginLeft ?? element.marginLeft ?? 0) : (localStyles.marginLeft ?? element.marginLeft ?? 0)}px`,
        marginRight: `${isMobileLayout ? (localStyles.marginRightMobile ?? element.marginRightMobile ?? localStyles.marginRight ?? element.marginRight ?? 0) : (localStyles.marginRight ?? element.marginRight ?? 0)}px`,
        cursor: isEditMode ? 'pointer' : 'default',
        transition: 'all 0.3s ease'
    };

    // 🎨 Text Styles
    const textStyles = {
        fontSize: `${activeTextFontSize}px`,
        fontWeight: activeTextFontWeight,
        fontFamily: element.textFontFamily || fonts.globalSmallFont || 'inherit',
        color: element.textColor || '#ffffff',
        transition: 'all 0.2s ease'
    };
    
    console.log('🎨 [UniversalCountdownBar] Applying text styles:', textStyles);

    // 🎨 Countdown Styles
    const countdownStyles = {
        fontSize: `${activeCountdownFontSize}px`,
        fontWeight: activeCountdownFontWeight,
        fontFamily: element.countdownFontFamily || fonts.globalBigFont || 'inherit',
        color: element.countdownColor || '#ffffff'
    };

    // 🎨 Timer Container Styles (OPTIMISTIC)
    const activeTimerPadding = isMobileLayout
        ? (localStyles.timerContainerPaddingMobile ?? element.timerContainerPaddingMobile ?? localStyles.timerContainerPadding ?? element.timerContainerPadding ?? 0)
        : (localStyles.timerContainerPadding ?? element.timerContainerPadding ?? 0);
    
    const activeTimerBorderRadius = isMobileLayout
        ? (localStyles.timerContainerBorderRadiusMobile ?? element.timerContainerBorderRadiusMobile ?? localStyles.timerContainerBorderRadius ?? element.timerContainerBorderRadius ?? 8)
        : (localStyles.timerContainerBorderRadius ?? element.timerContainerBorderRadius ?? 8);
    
    const activeTimerGap = isMobileLayout
        ? (localStyles.timerGapMobile ?? element.timerGapMobile ?? localStyles.timerGap ?? element.timerGap ?? 12)
        : (localStyles.timerGap ?? element.timerGap ?? 12);
    
    const timerContainerStyles = {
        padding: activeTimerPadding > 0 ? `${activeTimerPadding}px` : '0',
        borderRadius: `${activeTimerBorderRadius}px`,
        background: element.timerContainerBackgroundColor || 'transparent',
        border: element.timerContainerBorderEnabled
            ? `${localStyles.timerContainerBorderWidth ?? element.timerContainerBorderWidth ?? 2}px solid ${element.timerContainerBorderColor || '#ffffff'}`
            : 'none',
        boxShadow: element.timerContainerShadowEnabled
            ? `0 ${localStyles.timerShadowDistance ?? element.timerShadowDistance ?? 4}px ${localStyles.timerShadowIntensity ?? element.timerShadowIntensity ?? 10}px rgba(0, 0, 0, ${(localStyles.timerShadowOpacity ?? element.timerShadowOpacity ?? 15) / 100})`
            : 'none'
    };
    
    // 🎨 Timer Styles (inkl. Gap)
    const timerStyles = {
        ...countdownStyles,
        gap: `${activeTimerGap}px`
    };

    // 🎨 Format Display
    const showDays = element.showDays !== false;
    const showHours = element.showHours !== false;
    const showMinutes = element.showMinutes !== false;
    const showSeconds = element.showSeconds !== false;
    
    // 🎨 Layout & Position
    const timerLayout = element.timerLayout || 'horizontal'; // 'horizontal' or 'stacked'
    const timerPosition = element.timerPosition || 'right'; // 'left', 'center', 'right'
    
    // 🎨 Label Display Mode
    const labelDisplayMode = element.labelDisplayMode || 'short'; // 'short', 'long', 'none'
    
    // 🎨 Get Label Text based on mode
    const getLabel = (type) => {
        if (labelDisplayMode === 'none') return null;
        
        // ✅ Nutze custom labels falls vorhanden
        if (labelDisplayMode === 'custom') {
            const customLabels = {
                days: element.customDayLabel || 'd',
                hours: element.customHourLabel || 'h',
                minutes: element.customMinuteLabel || 'm',
                seconds: element.customSecondLabel || 's'
            };
            return customLabels[type];
        }
        
        // Standard labels
        const labels = {
            days: { short: 'd', long: 'Days' },
            hours: { short: 'h', long: 'Hours' },
            minutes: { short: 'm', long: 'Minutes' },
            seconds: { short: 's', long: 'Seconds' }
        };
        
        return labels[type][labelDisplayMode];
    };

    // 🎨 Render Timer Content
    const renderTimer = () => (
        <div 
            className={`countdown-bar-timer-container ${timerLayout === 'stacked' ? 'stacked' : ''}`}
            style={timerContainerStyles}
        >
            <div className="countdown-bar-timer" style={timerStyles}>
                {showDays && (
                    <div className="countdown-unit">
                        <span className="countdown-value">{String(timeLeft.days).padStart(2, '0')}</span>
                        {labelDisplayMode !== 'none' && (
                            <span className="countdown-label">{getLabel('days')}</span>
                        )}
                    </div>
                )}
                {showHours && (
                    <div className="countdown-unit">
                        <span className="countdown-value">{String(timeLeft.hours).padStart(2, '0')}</span>
                        {labelDisplayMode !== 'none' && (
                            <span className="countdown-label">{getLabel('hours')}</span>
                        )}
                    </div>
                )}
                {showMinutes && (
                    <div className="countdown-unit">
                        <span className="countdown-value">{String(timeLeft.minutes).padStart(2, '0')}</span>
                        {labelDisplayMode !== 'none' && (
                            <span className="countdown-label">{getLabel('minutes')}</span>
                        )}
                    </div>
                )}
                {showSeconds && (
                    <div className="countdown-unit">
                        <span className="countdown-value">{String(timeLeft.seconds).padStart(2, '0')}</span>
                        {labelDisplayMode !== 'none' && (
                            <span className="countdown-label">{getLabel('seconds')}</span>
                        )}
                    </div>
                )}
            </div>
        </div>
    );

    return (
        <div 
            ref={countdownRef}
            className={`universal-countdown-bar ${isEditMode ? 'edit-mode' : ''} ${isCurrentlyEditing ? 'is-editing' : ''} timer-position-${timerPosition}`}
            style={barStyles}
            onClick={handleClick}
        >
            {/* Dynamic Layout basierend auf timerPosition */}
            {timerPosition === 'left' && (
                <>
                    {renderTimer()}
                    <div className="countdown-bar-text" style={textStyles}>
                        <span
                            contentEditable={isEditMode}
                            suppressContentEditableWarning
                            onFocus={handleTextFocus}
                            onInput={handleTextInput}
                            onBlur={handleTextBlur}
                            className="countdown-text-content"
                        >
                            {editingText !== null ? editingText : (element.text || 'Limited Time Offer!')}
                        </span>
                    </div>
                </>
            )}
            
            {timerPosition === 'center' && (
                <div className="countdown-bar-centered">
                    <div className="countdown-bar-text" style={textStyles}>
                        <span
                            contentEditable={isEditMode}
                            suppressContentEditableWarning
                            onFocus={handleTextFocus}
                            onInput={handleTextInput}
                            onBlur={handleTextBlur}
                            className="countdown-text-content"
                        >
                            {editingText !== null ? editingText : (element.text || 'Limited Time Offer!')}
                        </span>
                    </div>
                    {renderTimer()}
                </div>
            )}
            
            {timerPosition === 'right' && (
                <>
                    <div className="countdown-bar-text" style={textStyles}>
                        <span
                            contentEditable={isEditMode}
                            suppressContentEditableWarning
                            onFocus={handleTextFocus}
                            onInput={handleTextInput}
                            onBlur={handleTextBlur}
                            className="countdown-text-content"
                        >
                            {editingText !== null ? editingText : (element.text || 'Limited Time Offer!')}
                        </span>
                    </div>
                    {renderTimer()}
                </>
            )}

            {/* 🎨 Floating Toolbar - Nur wenn aktiv editiert wird */}
            {isEditMode && showToolbar && isCurrentlyEditing && (
                <CountdownBarFloatingToolbar
                    element={optimisticElement}
                    onClose={handleToolbarClose}
                    onStyleChange={handleStyleChange}
                    colors={colors}
                    fonts={fonts}
                    anchorRef={countdownRef}
                    isMobileLayout={isMobileLayout}
                    isEditMode={isEditMode}
                    isSelected={isCurrentlyEditing}
                />
            )}
        </div>
    );
}

export default UniversalCountdownBar;

